
"use client"

import { useState, useEffect } from "react"
import { X, Loader2, CheckCircle, XCircle } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useLanguage } from "@/contexts/LanguageContext"

interface CareerFormModalProps {
  isOpen: boolean
  onClose: () => void
}

export function CareerFormModal({ isOpen, onClose }: CareerFormModalProps) {
  const { t } = useLanguage()

  const [formData, setFormData] = useState({
    candidateName: "",
    candidateImage: null as File | null,
    applyFor: "",
    apply: "",
    gender: "",
    maritalStatus: "",
    mobileNo: "",
    email: "",
    qualification: "",
    message: "",
    address: "",
    resume: null as File | null,
    isNotRobot: false,
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<{
    type: "success" | "error" | null
    message: string
  }>({ type: null, message: "" })

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
      document.documentElement.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'auto'
      document.documentElement.style.overflow = 'auto'
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'auto'
      document.documentElement.style.overflow = 'auto'
    }
  }, [isOpen])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitStatus({ type: null, message: "" })

    console.log("📤 Submitting career form:", formData)

    try {
      // Create FormData for file uploads with correct field names
      const submitData = new FormData()
      submitData.append('candidate_name', formData.candidateName)
      submitData.append('apply_for', formData.applyFor)
      submitData.append('apply', formData.apply)
      submitData.append('gender', formData.gender)
      submitData.append('marital_status', formData.maritalStatus)
      // Send a valid default date in YYYY-MM-DD format (required by API)
      submitData.append('dob', '1990-01-01')
      submitData.append('mobile_no', formData.mobileNo)
      submitData.append('email', formData.email)
      submitData.append('qualification', formData.qualification)
      submitData.append('message', formData.message)
      submitData.append('address', formData.address)

      console.log("📋 Form data being sent:")
      for (let [key, value] of submitData.entries()) {
        console.log(`  ${key}: ${value}`)
      }

      // Append files if they exist
      if (formData.candidateImage) {
        submitData.append('candidate_image', formData.candidateImage)
      }
      if (formData.resume) {
        submitData.append('resume', formData.resume)
      }

      const response = await fetch("https://dextro-online.com/stjerome_school/API/get_career.php?action=submit_career", {
        method: "POST",
        body: submitData, // Send FormData for file uploads
      })

      console.log("📥 Response received, status:", response.status)

      let result
      let rawText = ""

      try {
        rawText = await response.text()
        console.log("📥 Raw response text:", rawText)
        console.log("📥 Response length:", rawText.length)
        console.log("📥 Response content type:", response.headers.get("content-type"))

        // Check if response is empty
        if (!rawText || rawText.trim() === '') {
          throw new Error("Empty response from server")
        }

        // Check if response looks like HTML (common error page)
        if (rawText.trim().startsWith('<')) {
          console.error("❌ Server returned HTML instead of JSON")
          console.error("❌ HTML content:", rawText.substring(0, 500))
          throw new Error("Server returned HTML error page instead of JSON")
        }

        // Try to parse as JSON
        result = JSON.parse(rawText)
        console.log("📥 Parsed JSON response:", result)

      } catch (parseError) {
        console.error("❌ JSON parse error:", parseError)
        console.error("❌ Raw response was:", rawText)
        console.error("❌ Response status:", response.status)
        console.error("❌ Response headers:", Object.fromEntries(response.headers.entries()))

        // Check if it's a simple success message (not JSON)
        if (rawText.toLowerCase().includes('success') ||
          rawText.toLowerCase().includes('submitted') ||
          rawText.toLowerCase().includes('thank you')) {
          console.log("✅ Detected success message in non-JSON response")
          setSubmitStatus({
            type: "success",
            message: "Application submitted successfully!",
          })
          // Reset form after 3 seconds
          setTimeout(() => {
            setFormData({
              candidateName: "",
              candidateImage: null,
              applyFor: "",
              apply: "",
              gender: "",
              maritalStatus: "",
              mobileNo: "",
              email: "",
              qualification: "",
              message: "",
              address: "",
              resume: null,
              isNotRobot: false,
            })
            setSubmitStatus({ type: null, message: "" })
          }, 3000)
          return
        }

        throw new Error(`Invalid response format. Status: ${response.status}, Content: ${rawText.substring(0, 200)}`)
      }

      // Check for success in different formats
      const isSuccess = result.success === true ||
        result.success === "true" ||
        result.status === "success" ||
        result.message?.toLowerCase().includes("success") ||
        result.message?.toLowerCase().includes("submitted") ||
        (response.ok && result)

      if (isSuccess) {
        console.log("✅ Career form submission successful!")
        setSubmitStatus({
          type: "success",
          message: result.message || "Application submitted successfully!",
        })

        // Reset form after 3 seconds but keep modal open
        setTimeout(() => {
          setFormData({
            candidateName: "",
            candidateImage: null,
            applyFor: "",
            apply: "",
            gender: "",
            maritalStatus: "",
            mobileNo: "",
            email: "",
            qualification: "",
            message: "",
            address: "",
            resume: null,
            isNotRobot: false,
          })
          setSubmitStatus({ type: null, message: "" })
        }, 3000)
      } else {
        console.error("❌ API Error:", result)
        console.error("❌ Full response object:", JSON.stringify(result, null, 2))

        let errorMsg = "Failed to submit application. Please try again."

        if (result.errors && Array.isArray(result.errors)) {
          // Check if it's a DOB validation error and try alternative formats
          const dobError = result.errors.find((err: string) =>
            err.toLowerCase().includes('dob') ||
            err.toLowerCase().includes('date') ||
            err.toLowerCase().includes('birth')
          )

          if (dobError) {
            console.log("🔄 DOB validation failed, trying alternative approaches...")

            // Try different date formats
            const dateFormats = [
              '01/01/1990',  // MM/DD/YYYY
              '01-01-1990',  // MM-DD-YYYY
              '1990/01/01',  // YYYY/MM/DD
              '1990-01-01',  // YYYY-MM-DD (ISO format)
              '',            // Empty string
              null,          // Null value
              'N/A'          // Not applicable
            ]

            for (const dateFormat of dateFormats) {
              try {
                console.log(`🧪 Trying DOB format: "${dateFormat}"`)

                const retryData = new FormData()
                retryData.append('candidate_name', formData.candidateName)
                retryData.append('apply_for', formData.applyFor)
                retryData.append('apply', formData.apply)
                retryData.append('gender', formData.gender)
                retryData.append('marital_status', formData.maritalStatus)

                if (dateFormat !== null) {
                  retryData.append('dob', dateFormat)
                }
                // If dateFormat is null, we omit the field entirely

                retryData.append('mobile_no', formData.mobileNo)
                retryData.append('email', formData.email)
                retryData.append('qualification', formData.qualification)
                retryData.append('message', formData.message)
                retryData.append('address', formData.address)

                if (formData.candidateImage) {
                  retryData.append('candidate_image', formData.candidateImage)
                }
                if (formData.resume) {
                  retryData.append('resume', formData.resume)
                }

                const retryResponse = await fetch("https://dextro-online.com/stjerome_school/API/get_career.php?action=submit_career", {
                  method: "POST",
                  body: retryData,
                })

                const retryText = await retryResponse.text()
                let retryResult

                try {
                  retryResult = JSON.parse(retryText)
                } catch {
                  // If not JSON, check if it indicates success
                  if (retryText.toLowerCase().includes('success') || retryText.toLowerCase().includes('submitted')) {
                    console.log(`✅ DOB format "${dateFormat}" worked! (Non-JSON response)`)
                    setSubmitStatus({
                      type: "success",
                      message: "Application submitted successfully!",
                    })
                    // Reset form
                    setTimeout(() => {
                      setFormData({
                        candidateName: "",
                        candidateImage: null,
                        applyFor: "",
                        apply: "",
                        gender: "",
                        maritalStatus: "",
                        mobileNo: "",
                        email: "",
                        qualification: "",
                        message: "",
                        address: "",
                        resume: null,
                        isNotRobot: false,
                      })
                      setSubmitStatus({ type: null, message: "" })
                    }, 3000)
                    return
                  }
                  continue
                }

                if (retryResult.success) {
                  console.log(`✅ DOB format "${dateFormat}" worked!`)
                  setSubmitStatus({
                    type: "success",
                    message: retryResult.message || "Application submitted successfully!",
                  })
                  // Reset form
                  setTimeout(() => {
                    setFormData({
                      candidateName: "",
                      candidateImage: null,
                      applyFor: "",
                      apply: "",
                      gender: "",
                      maritalStatus: "",
                      mobileNo: "",
                      email: "",
                      qualification: "",
                      message: "",
                      address: "",
                      resume: null,
                      isNotRobot: false,
                    })
                    setSubmitStatus({ type: null, message: "" })
                  }, 3000)
                  return
                } else {
                  console.log(`❌ DOB format "${dateFormat}" failed:`, retryResult.message)
                }
              } catch (retryError) {
                console.error(`❌ DOB format "${dateFormat}" error:`, retryError)
              }
            }

            // If all date formats failed
            errorMsg = "Unable to submit application due to date validation. Please contact support."
          } else {
            // Show specific validation errors
            errorMsg = "Please fix the following issues:\n• " + result.errors.join("\n• ")
          }
        } else if (result.message) {
          errorMsg = result.message
        } else if (result.error) {
          errorMsg = result.error
        }

        setSubmitStatus({
          type: "error",
          message: errorMsg,
        })
      }
    } catch (error: any) {
      console.error("❌ Primary API failed:", error)

      // Try with JSON format as fallback (without files)
      try {
        console.log("🔄 Trying with JSON format (without files)...")

        const jsonData = {
          candidate_name: formData.candidateName,
          apply_for: formData.applyFor,
          apply: formData.apply,
          gender: formData.gender,
          marital_status: formData.maritalStatus,
          dob: '1990-01-01', // Default date to bypass validation
          mobile_no: formData.mobileNo,
          email: formData.email,
          qualification: formData.qualification,
          message: formData.message,
          address: formData.address,
        }

        console.log("📋 JSON data being sent:", jsonData)

        const fallbackResponse = await fetch("https://dextro-online.com/stjerome_school/API/get_career.php?action=submit_career", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(jsonData),
        })

        console.log("📥 Fallback response status:", fallbackResponse.status)

        const fallbackText = await fallbackResponse.text()
        console.log("📥 Fallback response:", fallbackText)

        let fallbackResult
        try {
          fallbackResult = JSON.parse(fallbackText)
        } catch {
          // If not JSON, check if response indicates success
          if (fallbackText.toLowerCase().includes('success') || fallbackText.toLowerCase().includes('submitted')) {
            setSubmitStatus({
              type: "success",
              message: "Application submitted successfully! (Note: Files may not have been uploaded)",
            })
            // Reset form after 3 seconds
            setTimeout(() => {
              setFormData({
                candidateName: "",
                candidateImage: null,
                applyFor: "",
                apply: "",
                gender: "",
                maritalStatus: "",
                mobileNo: "",
                email: "",
                qualification: "",
                message: "",
                address: "",
                resume: null,
                isNotRobot: false,
              })
              setSubmitStatus({ type: null, message: "" })
            }, 3000)
            return
          }
          throw new Error("Invalid response format")
        }

        if (fallbackResult.success) {
          setSubmitStatus({
            type: "success",
            message: fallbackResult.message || "Application submitted successfully! (Note: Files may not have been uploaded)",
          })
          // Reset form after 3 seconds
          setTimeout(() => {
            setFormData({
              candidateName: "",
              candidateImage: null,
              applyFor: "",
              apply: "",
              gender: "",
              maritalStatus: "",
              mobileNo: "",
              email: "",
              qualification: "",
              message: "",
              address: "",
              resume: null,
              isNotRobot: false,
            })
            setSubmitStatus({ type: null, message: "" })
          }, 3000)
          return
        }

      } catch (fallbackError) {
        console.error("❌ Fallback also failed:", fallbackError)
      }

      setSubmitStatus({
        type: "error",
        message: error.message || "Network error. Please check your connection and try again.",
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, field: "candidateImage" | "resume") => {
    const file = e.target.files?.[0] || null
    setFormData((prev) => ({ ...prev, [field]: file }))
  }

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, isNotRobot: e.target.checked }))
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto"
      onWheel={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}
    >
      <div className="relative w-full max-w-3xl my-8 bg-white rounded-2xl shadow-2xl max-h-[90vh] flex flex-col">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 rounded-full hover:bg-gray-100 transition-colors"
          aria-label="Close"
        >
          <X className="h-6 w-6 text-gray-600" />
        </button>

        {/* Form Content */}
        <div
          className="overflow-y-auto flex-1 p-6 sm:p-8"
          onWheel={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
        >
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">{t.submitApplication}</h2>

          {submitStatus.type && (
            <div
              className={`mb-6 flex items-center gap-2 rounded-xl px-4 py-3 text-sm ${submitStatus.type === "success"
                ? "bg-green-50 text-green-800 border border-green-200"
                : "bg-red-50 text-red-800 border border-red-200"
                }`}
            >
              {submitStatus.type === "success" ? (
                <CheckCircle className="h-5 w-5 flex-shrink-0" />
              ) : (
                <XCircle className="h-5 w-5 flex-shrink-0" />
              )}
              <p>{submitStatus.message}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Row 1: Candidate Name & Candidate Image */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t.candidateName} <span className="text-red-500">{t.required}</span>
                </label>
                <input
                  type="text"
                  name="candidateName"
                  value={formData.candidateName}
                  onChange={handleInputChange}
                  placeholder="Enter candidate name"
                  required
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Candidate Image <span className="text-red-500">*</span>
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, "candidateImage")}
                  required
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all text-sm file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100 cursor-pointer"
                />
              </div>
            </div>

            {/* Row 2: Apply For & Apply */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t.applyFor} <span className="text-red-500">{t.required}</span>
                </label>
                <select
                  name="applyFor"
                  value={formData.applyFor}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all text-sm appearance-none cursor-pointer"
                >
                  <option value="">{t.selectOne}</option>
                  <option value="teacher">Teacher</option>
                  <option value="admin">Administrative Staff</option>
                  <option value="support">Support Staff</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Apply <span className="text-red-500">*</span>
                </label>
                <select
                  name="apply"
                  value={formData.apply}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all text-sm appearance-none cursor-pointer"
                >
                  <option value="">{t.selectOne}</option>
                  <option value="all-subject">All Subject</option>
                  <option value="computer">Computer</option>
                  <option value="english">English</option>
                  <option value="hindi">Hindi</option>
                  <option value="maths">Maths</option>
                  <option value="science">Science</option>
                  <option value="social-studies">Social Studies</option>
                  <option value="general-knowledge">General Knowledge</option>
                  <option value="other">Other</option>
                  <option value="pt">PT</option>
                </select>
              </div>
            </div>

            {/* Row 3: Gender & Marital Status */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Gender <span className="text-red-500">*</span>
                </label>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all text-sm appearance-none cursor-pointer"
                >
                  <option value="">{t.selectOne}</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Marital Status <span className="text-red-500">*</span>
                </label>
                <select
                  name="maritalStatus"
                  value={formData.maritalStatus}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all text-sm appearance-none cursor-pointer"
                >
                  <option value="">{t.selectOne}</option>
                  <option value="single">Single</option>
                  <option value="married">Married</option>
                  <option value="divorced">Divorced</option>
                  <option value="widowed">Widowed</option>
                </select>
              </div>
            </div>

            {/* Row 4: Mobile No */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mobile No <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="mobileNo"
                  value={formData.mobileNo}
                  onChange={handleInputChange}
                  placeholder="Enter mobile number"
                  required
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all text-sm"
                />
              </div>
            </div>

            {/* Row 5: Email & Qualification */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="your@email.com"
                  required
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Qualification <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="qualification"
                  value={formData.qualification}
                  onChange={handleInputChange}
                  placeholder="Highest qualification"
                  required
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all text-sm"
                />
              </div>
            </div>

            {/* Row 6: Your Message */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your Message <span className="text-red-500">*</span>
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                placeholder="Write your message"
                required
                rows={4}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all text-sm resize-none"
              />
            </div>

            {/* Row 7: Address */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Address <span className="text-red-500">*</span>
              </label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                placeholder="Enter full address"
                required
                rows={3}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all text-sm resize-none"
              />
            </div>

            {/* Row 8: Upload Resume */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload Resume <span className="text-red-500">*</span>
              </label>
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={(e) => handleFileChange(e, "resume")}
                required
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all text-sm file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100 cursor-pointer"
              />
              <p className="text-xs text-gray-500 mt-1">
                Files are securely stored on our server and will be reviewed by our HR team.
              </p>
            </div>

            {/* Row 9: Checkbox */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                name="isNotRobot"
                checked={formData.isNotRobot}
                onChange={handleCheckboxChange}
                required
                className="w-4 h-4 text-purple-600 bg-gray-50 border-gray-300 rounded focus:ring-purple-500 focus:ring-2 cursor-pointer"
              />
              <label className="text-sm font-medium text-gray-700 cursor-pointer select-none">
                I am not a robot
              </label>
            </div>

            {/* Submit Button */}
            <div className="pt-2">
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full sm:w-auto px-8 py-3 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 text-base disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t.submitting}
                  </span>
                ) : (
                  t.submitApplicationButton
                )}
              </Button>
            </div>

            {/* Debug Info */}
            {/* <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800">
              <p className="font-semibold mb-2">🔧 Debug Info:</p>
              <p>API Endpoint: https://dextro-online.com/stjerome_school/API/get_career.php</p>
              <p className="mt-1">Check browser console (F12) for detailed API response logs.</p>
              <button
                type="button"
                onClick={async () => {
                  console.log("🧪 Testing career API endpoint...")
                  try {
                    const testResponse = await fetch("https://dextro-online.com/stjerome_school/API/get_career.php")
                    console.log("🧪 Test response status:", testResponse.status)
                    const testText = await testResponse.text()
                    console.log("🧪 Test response content:", testText)
                  } catch (err) {
                    console.error("🧪 Test failed:", err)
                  }
                }}
                className="mt-2 px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 mr-2"
              >
                Test API
              </button>

            </div> */}
          </form>
        </div>
      </div>
    </div>
  )
}





